/**
 * Storyboard — the director's board. Rebuilt by the Storyboard fleet
 * agent to match the 2026-06-26 mockups (storyboard-grid.png,
 * storyboard-frame-info.png) and to OWN the per-frame camera-angle
 * adjuster (the standalone Camera Lab panel was dropped — _GLOBAL.md
 * G7/G8). Uses the FROZEN shared components + foundation store APIs.
 *
 * Two views, toggled in-panel:
 *
 *   GRID (storyboard-grid.png) — a 3-column grid of frame cards across
 *   the whole film. Each card: a maroon numbered badge (top-left), an
 *   "N of TOTAL" running counter (top-right), the frame image with
 *   **Edit** + **Revert** buttons overlaid, and a "Description" label +
 *   caption beneath. Frames are grouped per scene (add / drag-reorder
 *   within a scene). PROGRESSIVE-FILL gate: a scene's frames grey out
 *   until its prereqs (cast + location) are met, with inline "fix" chips
 *   that jump to the missing tool — the board is always visible.
 *
 *   FRAME INFO (storyboard-frame-info.png) — opening a frame shows the
 *   large hero image + tabs Character | Location | Assets. Each tab lists
 *   what's in the frame (derived from the scene's `@`-refs / cast /
 *   location, editable per frame) with a dashed "+ Add", and characters
 *   carry a "+ Outfit" control (pick the costume look). The hero toolbar
 *   wires the shared Photoshop-style image editor (Edit), the shared
 *   camera-angle adjuster (Adjust Camera Angle), and Generate/Regenerate.
 *
 * Generation routes through the shared `runGeneration` stub (real loading
 * → ready states, placeholder asset, single provider seam). Ready frames
 * mark `scene.storyboardReady` + the overall `storyboardStatus` so the
 * Script Editor, Timeline, and Final Video reflect progress.
 */

import { BasePanel } from "./BasePanel";
import { buildEmptyState, buildHelperParagraph, buildPixelBox, buildStudioButton } from "./dom";
import type { PanelManager, StudioPanelId } from "../PanelManager";
import type { StoryboardFrame, StudioScene, StudioStore } from "../StudioStore";
import {
  describeShot,
  openCameraAngleAdjuster,
  openEntityDrawer,
  openFormModal,
  openImageEditor,
  runGeneration,
  type CameraAngle,
  type FormModalHandle,
} from "../shared";
import "../styles/storyboard.css";

interface StoryboardPanelOptions {
  store: StudioStore;
  panelManager: PanelManager;
}

interface Missing {
  label: string;
  jumpTo: StudioPanelId;
}

/**
 * The persisted frame shape PLUS the fields this panel owns. The
 * foundation `StoryboardFrame` is the canonical record; these extra
 * fields ride along on the same object through `setStoryboardFrames`
 * (the store stores frame objects verbatim). The integrator should fold
 * these into the real `StoryboardFrame` interface at merge — see the
 * reconcile notes in the handoff.
 *
 * Override semantics: when a key is ABSENT the value is DERIVED from the
 * scene (cast from dialogue/@refs, location from the slug, assets from
 * @refs). When present it is an explicit per-frame override.
 */
interface SbFrame extends StoryboardFrame {
  /** Per-frame camera angle (pan/tilt/dolly). Absent ⇒ default. */
  cameraAngle?: CameraAngle | null;
  /** One level of image undo for the card-level Revert button. */
  previousImage?: string | null;
  /** Per-frame cast override (character ids). Absent ⇒ derive from scene. */
  cast?: string[] | null;
  /** Per-frame location override. Absent ⇒ derive from scene slug. */
  locationId?: string | null;
  /** Per-frame asset ids. Absent ⇒ derive from scene @-refs. */
  assets?: string[] | null;
  /** characterId → chosen costume lookId, per frame. */
  outfitByCharacter?: Record<string, string>;
}

type FrameTab = "character" | "location" | "assets";

export class StoryboardPanel extends BasePanel {
  private readonly store: StudioStore;
  private readonly panelManager: PanelManager;
  private boardEl: HTMLDivElement | null = null;
  private storeUnsubscribers: (() => void)[] = [];
  private generating = new Set<string>();

  /** View routing. */
  private view: "grid" | "frame" = "grid";
  private activeSceneId: string | null = null;
  private activeFrameId: string | null = null;
  private frameTab: FrameTab = "character";

  /** Drag-reorder scratch (frame being dragged, and its scene). */
  private dragSceneId: string | null = null;
  private dragFrameId: string | null = null;

  /** Open inline add/outfit menu (only one at a time). */
  private openMenu: HTMLElement | null = null;
  private frameModal: FormModalHandle | null = null;
  private frameModalSubmitted = false;
  private frameModalDeleted = false;
  private frameSnapshot: SbFrame | null = null;

  constructor(options: StoryboardPanelOptions) {
    super();
    this.store = options.store;
    this.panelManager = options.panelManager;
  }

  protected buildRootElement(): HTMLElement {
    const root = document.createElement("div");
    root.className = "sb-root";
    const box = buildPixelBox();
    const board = document.createElement("div");
    board.className = "sb-view";
    box.fill.appendChild(board);
    this.boardEl = board;
    root.appendChild(box.outer);
    return root;
  }

  protected onMounted(): void {
    const keys = [
      "scenes",
      "castingBySceneId",
      "locations",
      "characters",
      "assets",
      "costumeLooksByCharacterId",
      "storyboardFramesBySceneId",
      "entityRefs",
      "shotVariationByFrameId",
    ] as const;
    for (const key of keys) {
      this.storeUnsubscribers.push(this.store.subscribe(key, () => this.render()));
    }
    this.render();
  }

  protected onShown(): void {
    this.render();
  }

  protected onBeforeDestroy(): void {
    for (const unsub of this.storeUnsubscribers) unsub();
    this.storeUnsubscribers = [];
  }

  // ------------------------------------------------------------------
  // Top-level render / routing
  // ------------------------------------------------------------------

  private render(): void {
    if (this.view === "frame" && this.frameModal) {
      this.frameModal.body.replaceChildren(this.buildFrameInfo());
      return;
    }
    if (!this.boardEl) return;
    this.closeMenu();
    this.boardEl.replaceChildren();

    const scenes = this.store.get("scenes");
    if (scenes.length === 0) {
      this.view = "grid";
      this.boardEl.appendChild(
        buildEmptyState("No scenes yet — write or upload a script, then board each shot here."),
      );
      return;
    }

    if (this.view === "frame" && this.activeFrame()) {
      this.boardEl.appendChild(this.buildFrameInfo());
      return;
    }
    // Fall back to the grid if the active frame vanished.
    this.view = "grid";
    this.boardEl.appendChild(this.buildGrid(scenes));
  }

  // ------------------------------------------------------------------
  // GRID view (storyboard-grid.png)
  // ------------------------------------------------------------------

  private buildGrid(scenes: StudioScene[]): HTMLElement {
    const wrap = document.createElement("div");
    wrap.className = "sb-grid-wrap";
    wrap.appendChild(
      buildHelperParagraph(
        "Your storyboard — every shot across the film. Add frames to a scene, write what happens, then generate the panel. Frames grey out until a scene has its cast and location; click a fix chip to set them. Open a frame to manage its cast, location, assets, and camera angle.",
      ),
    );

    const total = scenes.reduce((sum, s) => sum + this.framesFor(s.id).length, 0);
    let running = 0;

    scenes.forEach((scene, sceneIndex) => {
      const sceneNumber = sceneIndex + 1;
      const section = document.createElement("section");
      section.className = "sb-scene";

      // ---- Scene header: number + heading + fix chips + add ---------
      const head = document.createElement("div");
      head.className = "sb-scene-head";
      const title = document.createElement("span");
      title.className = "sb-scene-title";
      title.textContent = `Scene ${sceneNumber} — ${scene.heading || "UNTITLED"}`;
      head.appendChild(title);

      const missing = this.prerequisites(scene);
      if (missing.length > 0) {
        const chips = document.createElement("span");
        chips.className = "sb-fix-chips";
        for (const m of missing) {
          const chip = buildStudioButton(`⚠ Fix ${m.label}`, {
            onClick: () => this.panelManager.open(m.jumpTo),
          });
          chip.classList.add("sb-fix-chip");
          chip.dataset.kokoAction = "storyboard-fix";
          chip.dataset.fix = m.label;
          chips.appendChild(chip);
        }
        head.appendChild(chips);
      }

      const addFrame = buildStudioButton("+ Shot", {
        onClick: () => this.openAddFrameModal(scene),
      });
      addFrame.classList.add("sb-add-shot");
      addFrame.dataset.kokoAction = "storyboard-add-frame";
      addFrame.dataset.id = scene.id;
      head.appendChild(addFrame);
      section.appendChild(head);

      // ---- 3-col grid of frame cards --------------------------------
      const grid = document.createElement("div");
      grid.className = "sb-grid";
      const frames = this.framesFor(scene.id).slice().sort((a, b) => a.order - b.order);
      if (frames.length === 0) {
        const empty = document.createElement("div");
        empty.className = "sb-grid-empty";
        empty.textContent = "No shots yet — add one with “+ Shot”.";
        grid.appendChild(empty);
      } else {
        for (const frame of frames) {
          running += 1;
          grid.appendChild(this.buildCard(scene, frame, running, total, missing));
        }
      }
      section.appendChild(grid);
      wrap.appendChild(section);
    });
    return wrap;
  }

  private buildCard(
    scene: StudioScene,
    frame: SbFrame,
    number: number,
    total: number,
    missing: Missing[],
  ): HTMLElement {
    const blocked = missing.length > 0;
    const genKey = `${scene.id}:${frame.id}`;
    const isGenerating = this.generating.has(genKey);

    const card = document.createElement("article");
    card.className = "sb-card";
    if (blocked) card.classList.add("is-blocked");
    card.dataset.frameId = frame.id;
    card.dataset.sceneId = scene.id;
    card.draggable = true;
    this.wireDrag(card, scene.id, frame.id);

    // Header row: numbered badge (left) + running counter (right).
    const headRow = document.createElement("div");
    headRow.className = "sb-card-head";
    const badge = document.createElement("span");
    badge.className = "sb-card-badge";
    badge.textContent = String(number);
    headRow.appendChild(badge);
    const counter = document.createElement("span");
    counter.className = "sb-card-counter";
    counter.textContent = `${number} of ${total}`;
    headRow.appendChild(counter);
    card.appendChild(headRow);

    // Media
    const media = document.createElement("div");
    media.className = "sb-card-media";
    media.dataset.kokoAction = "storyboard-open-frame";
    media.dataset.frameId = frame.id;
    media.addEventListener("click", () => this.openFrame(scene.id, frame.id));

    if (isGenerating) {
      media.classList.add("is-loading");
      const spinner = document.createElement("span");
      spinner.className = "sb-card-loading";
      spinner.textContent = "generating…";
      media.appendChild(spinner);
    } else if (frame.image) {
      media.classList.add("is-ready");
      media.style.backgroundImage = `url("${frame.image}")`;

      const overlay = document.createElement("div");
      overlay.className = "sb-card-overlay";
      const edit = this.overlayButton("✎ Edit", "storyboard-edit-image", () =>
        this.editImage(scene, frame),
      );
      const revert = this.overlayButton("↺ Revert", "storyboard-revert", () =>
        this.revertImage(scene.id, frame),
      );
      revert.classList.add("is-revert");
      if (!frame.previousImage) revert.disabled = true;
      overlay.appendChild(edit);
      overlay.appendChild(revert);
      media.appendChild(overlay);
    } else {
      media.classList.add("is-empty");
      const placeholder = document.createElement("div");
      placeholder.className = "sb-card-placeholder";
      placeholder.textContent = blocked ? "blocked" : "empty";
      media.appendChild(placeholder);
      const gen = this.overlayButton(
        "✦ Generate",
        "storyboard-generate",
        () => void this.generate(scene, frame),
        true,
      );
      gen.classList.add("sb-card-generate");
      gen.disabled = blocked;
      gen.title = blocked
        ? "Set this scene's cast and location first"
        : "Generate this frame (placeholder)";
      media.appendChild(gen);
    }
    card.appendChild(media);

    // Description
    const desc = document.createElement("div");
    desc.className = "sb-card-desc";
    const label = document.createElement("div");
    label.className = "sb-card-desc-label";
    label.textContent = "Description";
    desc.appendChild(label);

    const caption = document.createElement("div");
    caption.className = "sb-card-desc-text";
    caption.contentEditable = "true";
    caption.spellcheck = false;
    caption.dataset.kokoAction = "storyboard-caption";
    caption.dataset.placeholder = "Describe what happens in this shot…";
    caption.textContent = frame.caption;
    caption.addEventListener("click", (e) => e.stopPropagation());
    caption.addEventListener("blur", () => {
      const text = caption.textContent ?? "";
      if (text !== frame.caption) this.updateFrame(scene.id, frame.id, { caption: text });
    });
    desc.appendChild(caption);

    if (frame.retakes && frame.retakes > 0) {
      const retakes = document.createElement("span");
      retakes.className = "sb-card-retakes";
      retakes.textContent = `↻ ${frame.retakes} retake${frame.retakes > 1 ? "s" : ""}`;
      desc.appendChild(retakes);
    }
    card.appendChild(desc);

    return card;
  }

  private overlayButton(
    label: string,
    action: string,
    onClick: () => void,
    centered = false,
  ): HTMLButtonElement {
    const button = document.createElement("button");
    button.type = "button";
    button.className = centered ? "sb-card-cta" : "sb-overlay-btn";
    button.textContent = label;
    button.dataset.kokoAction = action;
    button.addEventListener("click", (event) => {
      event.stopPropagation();
      onClick();
    });
    return button;
  }

  // ------------------------------------------------------------------
  // FRAME INFO view (storyboard-frame-info.png)
  // ------------------------------------------------------------------

  private buildFrameInfo(): HTMLElement {
    const ctx = this.activeFrame()!;
    const { scene, frame } = ctx;
    const wrap = document.createElement("div");
    wrap.className = "sb-info";

    // ---- Top bar: back + scene label -------------------------------
    const top = document.createElement("div");
    top.className = "sb-info-top";
    const back = buildStudioButton("← Board", { onClick: () => this.closeFrame() });
    back.dataset.kokoAction = "storyboard-back";
    top.appendChild(back);
    const crumb = document.createElement("span");
    crumb.className = "sb-info-crumb";
    crumb.textContent = scene.heading || "UNTITLED SCENE";
    top.appendChild(crumb);
    wrap.appendChild(top);

    // ---- Hero image ------------------------------------------------
    const hero = document.createElement("div");
    hero.className = "sb-info-hero";
    const genKey = `${scene.id}:${frame.id}`;
    if (this.generating.has(genKey)) {
      hero.classList.add("is-loading");
      hero.textContent = "generating…";
    } else if (frame.image) {
      hero.style.backgroundImage = `url("${frame.image}")`;
      hero.classList.add("is-ready");
    } else {
      hero.classList.add("is-empty");
      hero.textContent = "No image yet — generate this frame.";
    }
    wrap.appendChild(hero);

    // ---- Hero toolbar ----------------------------------------------
    const toolbar = document.createElement("div");
    toolbar.className = "sb-info-toolbar";
    const gen = buildStudioButton(frame.image ? "↻ Regenerate" : "✦ Generate", {
      variant: "primary",
      onClick: () => void this.generate(scene, frame),
    });
    gen.dataset.kokoAction = "storyboard-generate";
    gen.disabled = this.generating.has(genKey) || this.prerequisites(scene).length > 0;
    toolbar.appendChild(gen);

    const edit = buildStudioButton("✎ Edit Image", { onClick: () => this.editImage(scene, frame) });
    edit.dataset.kokoAction = "storyboard-edit-image";
    edit.disabled = !frame.image;
    toolbar.appendChild(edit);

    const cam = buildStudioButton("⌖ Adjust Camera Angle", {
      onClick: () => this.adjustCamera(scene, frame),
    });
    cam.dataset.kokoAction = "storyboard-camera";
    toolbar.appendChild(cam);

    const shot = document.createElement("span");
    shot.className = "sb-info-shot";
    shot.textContent = describeShot(frame.cameraAngle ?? { pan: 0, tilt: 0, dolly: 8 });
    toolbar.appendChild(shot);

    const del = buildStudioButton("✕ Delete frame", {
      variant: "danger",
      onClick: () => {
        this.frameModalDeleted = true;
        this.removeFrame(scene.id, frame.id);
        this.closeFrame();
      },
    });
    del.dataset.kokoAction = "storyboard-delete-frame";
    del.classList.add("sb-info-delete");
    toolbar.appendChild(del);
    wrap.appendChild(toolbar);

    // ---- Tabs ------------------------------------------------------
    const tabs = document.createElement("div");
    tabs.className = "sb-info-tabs";
    const tabDefs: Array<{ id: FrameTab; label: string }> = [
      { id: "character", label: "Character" },
      { id: "location", label: "Location" },
      { id: "assets", label: "Assets" },
    ];
    for (const def of tabDefs) {
      const tab = document.createElement("button");
      tab.type = "button";
      tab.className = "sb-info-tab";
      tab.textContent = def.label;
      tab.dataset.kokoAction = "storyboard-tab";
      tab.dataset.tab = def.id;
      if (def.id === this.frameTab) tab.classList.add("is-active");
      tab.addEventListener("click", () => {
        this.frameTab = def.id;
        this.render();
      });
      tabs.appendChild(tab);
    }
    wrap.appendChild(tabs);

    // ---- Tab body --------------------------------------------------
    const body = document.createElement("div");
    body.className = "sb-info-tabbody";
    body.dataset.tab = this.frameTab;
    if (this.frameTab === "character") this.buildCharacterTab(body, scene, frame);
    else if (this.frameTab === "location") this.buildLocationTab(body, scene, frame);
    else this.buildAssetsTab(body, scene, frame);
    wrap.appendChild(body);

    return wrap;
  }

  private buildCharacterTab(body: HTMLElement, scene: StudioScene, frame: SbFrame): void {
    const ids = this.frameCast(scene, frame);
    const characters = this.store.get("characters");
    for (const id of ids) {
      const character = characters.find((c) => c.id === id);
      if (!character) continue;
      const card = document.createElement("div");
      card.className = "sb-entity-card";
      card.dataset.kokoAction = "storyboard-entity";
      card.dataset.entityId = id;

      const thumb = document.createElement("div");
      thumb.className = "sb-entity-thumb";
      if (character.referenceImage) {
        thumb.style.backgroundImage = `url("${character.referenceImage}")`;
      } else {
        thumb.classList.add("is-initials");
        thumb.textContent = character.name.slice(0, 2).toUpperCase();
      }
      thumb.title = `Edit ${character.name}`;
      thumb.addEventListener("click", () => this.openCharacterDrawer(id));
      card.appendChild(thumb);

      const name = document.createElement("div");
      name.className = "sb-entity-name";
      name.textContent = character.name;
      card.appendChild(name);

      // + Outfit (pick costume look)
      const outfitRow = document.createElement("div");
      outfitRow.className = "sb-entity-outfit";
      const outfitText = this.outfitLabel(frame, id);
      const outfit = document.createElement("button");
      outfit.type = "button";
      outfit.className = "sb-outfit-btn";
      outfit.dataset.kokoAction = "storyboard-add-outfit";
      outfit.dataset.entityId = id;
      outfit.textContent = outfitText ? `👕 ${outfitText}` : "👕 + Outfit";
      outfit.addEventListener("click", (event) =>
        this.openOutfitMenu(event.currentTarget as HTMLElement, scene, frame, id),
      );
      outfitRow.appendChild(outfit);
      card.appendChild(outfitRow);

      // Remove from frame
      const remove = document.createElement("button");
      remove.type = "button";
      remove.className = "sb-entity-remove";
      remove.dataset.kokoAction = "storyboard-remove-entity";
      remove.title = "Remove from frame";
      remove.textContent = "×";
      remove.addEventListener("click", () =>
        this.setFrameCast(
          scene,
          frame,
          ids.filter((x) => x !== id),
        ),
      );
      card.appendChild(remove);

      body.appendChild(card);
    }

    const candidates = characters
      .filter((c) => !ids.includes(c.id))
      .map((c) => ({ id: c.id, label: c.name }));
    body.appendChild(
      this.buildAddCard("Character", candidates, (chosen) =>
        this.setFrameCast(scene, frame, [...ids, chosen]),
      ),
    );
  }

  private buildLocationTab(body: HTMLElement, scene: StudioScene, frame: SbFrame): void {
    const locationId = this.frameLocation(scene, frame);
    const locations = this.store.get("locations");
    const current = locationId ? locations.find((l) => l.id === locationId) : null;

    if (current) {
      const card = document.createElement("div");
      card.className = "sb-entity-card is-location";
      card.dataset.kokoAction = "storyboard-entity";
      card.dataset.entityId = current.id;

      const thumb = document.createElement("div");
      thumb.className = "sb-entity-thumb";
      const plate = current.referenceImage ?? current.panoramaImage ?? null;
      if (plate) thumb.style.backgroundImage = `url("${plate}")`;
      else {
        thumb.classList.add("is-initials");
        thumb.textContent = current.name.slice(0, 2).toUpperCase();
      }
      thumb.addEventListener("click", () => this.panelManager.open("location"));
      card.appendChild(thumb);

      const name = document.createElement("div");
      name.className = "sb-entity-name";
      name.textContent = current.name;
      card.appendChild(name);

      // Per-shot location VARIATION (setShotVariation — store API).
      const variations = current.variations ?? [];
      if (variations.length > 0) {
        const varRow = document.createElement("div");
        varRow.className = "sb-entity-outfit";
        const activeVar = this.store.get("shotVariationByFrameId")[frame.id] ?? null;
        const varBtn = document.createElement("button");
        varBtn.type = "button";
        varBtn.className = "sb-outfit-btn";
        varBtn.dataset.kokoAction = "storyboard-shot-variation";
        const activeLabel = variations.find((v) => v.id === activeVar)?.label;
        varBtn.textContent = activeLabel ? `🕑 ${activeLabel}` : "🕑 + Variation";
        const options = [
          { id: "__base__", label: "Base plate" },
          ...variations.map((v) => ({ id: v.id, label: v.label })),
        ];
        varBtn.addEventListener("click", (event) =>
          this.openMenuFor(event.currentTarget as HTMLElement, options, (chosen) => {
            this.store.setShotVariation(frame.id, chosen === "__base__" ? null : chosen);
          }),
        );
        varRow.appendChild(varBtn);
        card.appendChild(varRow);
      }

      const remove = document.createElement("button");
      remove.type = "button";
      remove.className = "sb-entity-remove";
      remove.dataset.kokoAction = "storyboard-remove-entity";
      remove.title = "Clear location";
      remove.textContent = "×";
      remove.addEventListener("click", () => this.setFrameLocation(scene, frame, null));
      card.appendChild(remove);

      body.appendChild(card);
    }

    const candidates = locations
      .filter((l) => l.id !== locationId)
      .map((l) => ({ id: l.id, label: l.name }));
    body.appendChild(
      this.buildAddCard("Location", candidates, (chosen) =>
        this.setFrameLocation(scene, frame, chosen),
      ),
    );
  }

  private buildAssetsTab(body: HTMLElement, scene: StudioScene, frame: SbFrame): void {
    const ids = this.frameAssets(scene, frame);
    const assets = this.store.get("assets");
    for (const id of ids) {
      const asset = assets.find((a) => a.id === id);
      if (!asset) continue;
      const card = document.createElement("div");
      card.className = "sb-entity-card is-asset";
      card.dataset.kokoAction = "storyboard-entity";
      card.dataset.entityId = id;

      const thumb = document.createElement("div");
      thumb.className = "sb-entity-thumb";
      if (asset.referenceImage) thumb.style.backgroundImage = `url("${asset.referenceImage}")`;
      else {
        thumb.classList.add("is-initials");
        thumb.textContent = asset.name.slice(0, 2).toUpperCase();
      }
      thumb.addEventListener("click", () => this.panelManager.open("assets"));
      card.appendChild(thumb);

      const name = document.createElement("div");
      name.className = "sb-entity-name";
      name.textContent = asset.name;
      card.appendChild(name);

      const remove = document.createElement("button");
      remove.type = "button";
      remove.className = "sb-entity-remove";
      remove.dataset.kokoAction = "storyboard-remove-entity";
      remove.title = "Remove from frame";
      remove.textContent = "×";
      remove.addEventListener("click", () =>
        this.setFrameAssets(
          scene,
          frame,
          ids.filter((x) => x !== id),
        ),
      );
      card.appendChild(remove);

      body.appendChild(card);
    }

    const candidates = assets
      .filter((a) => !ids.includes(a.id))
      .map((a) => ({ id: a.id, label: a.name }));
    body.appendChild(
      this.buildAddCard("Asset", candidates, (chosen) =>
        this.setFrameAssets(scene, frame, [...ids, chosen]),
      ),
    );
  }

  /** A dashed "+ Add" card that opens a picker of available entities. */
  private buildAddCard(
    kind: string,
    candidates: Array<{ id: string; label: string }>,
    onPick: (id: string) => void,
  ): HTMLElement {
    const card = document.createElement("button");
    card.type = "button";
    card.className = "sb-add-card";
    card.dataset.kokoAction = "storyboard-add-entity";
    card.dataset.kind = kind.toLowerCase();
    const plus = document.createElement("span");
    plus.className = "sb-add-plus";
    plus.textContent = "+";
    const text = document.createElement("span");
    text.className = "sb-add-text";
    text.textContent = "Add";
    card.appendChild(plus);
    card.appendChild(text);
    card.addEventListener("click", (event) => {
      if (candidates.length === 0) {
        this.openMenuFor(event.currentTarget as HTMLElement, [
          { id: "__none__", label: `No more ${kind.toLowerCase()}s available` },
        ]);
        return;
      }
      this.openMenuFor(event.currentTarget as HTMLElement, candidates, onPick);
    });
    return card;
  }

  // ------------------------------------------------------------------
  // Inline menu (add / outfit / variation pickers)
  // ------------------------------------------------------------------

  private openOutfitMenu(
    anchor: HTMLElement,
    scene: StudioScene,
    frame: SbFrame,
    characterId: string,
  ): void {
    const looks = this.store.get("costumeLooksByCharacterId")[characterId] ?? [];
    const character = this.store.get("characters").find((c) => c.id === characterId);
    const options: Array<{ id: string; label: string }> = [];
    if (character?.primaryLookId) {
      const primary = looks.find((l) => l.id === character.primaryLookId);
      options.push({ id: character.primaryLookId, label: primary?.note || "Primary look" });
    }
    for (const look of looks) {
      if (look.id === character?.primaryLookId) continue;
      options.push({ id: look.id, label: look.note || "Look" });
    }
    if (options.length === 0) {
      this.openMenuFor(anchor, [{ id: "__none__", label: "No costume looks yet — add in Costume" }]);
      return;
    }
    options.unshift({ id: "__clear__", label: "Default outfit" });
    this.openMenuFor(anchor, options, (chosen) => {
      const next = { ...(frame.outfitByCharacter ?? {}) };
      if (chosen === "__clear__") delete next[characterId];
      else next[characterId] = chosen;
      this.updateFrame(scene.id, frame.id, { outfitByCharacter: next });
    });
  }

  private openMenuFor(
    anchor: HTMLElement,
    options: Array<{ id: string; label: string }>,
    onPick?: (id: string) => void,
  ): void {
    this.closeMenu();
    const menu = document.createElement("div");
    menu.className = "sb-menu";
    menu.dataset.kokoSurface = "storyboard-menu";
    for (const opt of options) {
      const item = document.createElement("button");
      item.type = "button";
      item.className = "sb-menu-item";
      item.dataset.kokoAction = "storyboard-menu-item";
      item.dataset.optionId = opt.id;
      item.textContent = opt.label;
      const inert = opt.id.startsWith("__none__");
      if (inert) item.disabled = true;
      item.addEventListener("click", (event) => {
        event.stopPropagation();
        if (!inert && onPick) onPick(opt.id);
        this.closeMenu();
        if (!inert) this.render();
      });
      menu.appendChild(item);
    }
    const rect = anchor.getBoundingClientRect();
    const host = this.boardEl ?? document.body;
    const hostRect = host.getBoundingClientRect();
    menu.style.left = `${rect.left - hostRect.left}px`;
    menu.style.top = `${rect.bottom - hostRect.top + 4}px`;
    host.appendChild(menu);
    this.openMenu = menu;
    // Close on next outside click.
    requestAnimationFrame(() => {
      const onDoc = (e: MouseEvent): void => {
        if (this.openMenu && !this.openMenu.contains(e.target as Node)) {
          this.closeMenu();
          document.removeEventListener("click", onDoc, true);
        }
      };
      document.addEventListener("click", onDoc, true);
    });
  }

  private closeMenu(): void {
    this.openMenu?.remove();
    this.openMenu = null;
  }

  // ------------------------------------------------------------------
  // Shared-component wiring (image editor + camera-angle adjuster)
  // ------------------------------------------------------------------

  private editImage(scene: StudioScene, frame: SbFrame): void {
    openImageEditor({
      image: frame.image ?? "",
      title: `Edit shot — ${scene.heading || "frame"}`,
      promptPlaceholder: "Describe how you want to modify this scene image…",
      onChange: (next) => {
        const cur = this.frameById(scene.id, frame.id);
        if (!cur || next === cur.image) return;
        this.updateFrame(scene.id, frame.id, {
          previousImage: cur.image ?? null,
          image: next,
        });
      },
    });
  }

  private revertImage(sceneId: string, frame: SbFrame): void {
    if (!frame.previousImage) return;
    this.updateFrame(sceneId, frame.id, {
      image: frame.previousImage,
      previousImage: null,
    });
    this.recomputeStatus();
  }

  private adjustCamera(scene: StudioScene, frame: SbFrame): void {
    openCameraAngleAdjuster({
      image: frame.image,
      angle: frame.cameraAngle ?? undefined,
      onApply: (angle) => {
        this.updateFrame(scene.id, frame.id, { cameraAngle: angle });
        void this.generate(scene, this.frameById(scene.id, frame.id) ?? frame, angle);
      },
    });
  }

  private openCharacterDrawer(characterId: string): void {
    const character = this.store.get("characters").find((c) => c.id === characterId);
    if (!character) return;
    openEntityDrawer({
      title: `Edit ${character.name}`,
      subtitle: "Character",
      render: (drawerBody) => {
        const p = document.createElement("p");
        p.className = "studio-helper";
        p.textContent =
          character.description || "Open the Character Creator to edit this character's details.";
        drawerBody.appendChild(p);
        const open = buildStudioButton("Open in Character Creator", {
          variant: "primary",
          onClick: () => this.panelManager.open("characters"),
        });
        drawerBody.appendChild(open);
      },
    });
  }

  // ------------------------------------------------------------------
  // Per-frame derivation (cast / location / assets)
  // ------------------------------------------------------------------

  private frameCast(scene: StudioScene, frame: SbFrame): string[] {
    if (Array.isArray(frame.cast)) return frame.cast;
    return this.store.getCastingForScene(scene.id);
  }

  private frameLocation(scene: StudioScene, frame: SbFrame): string | null {
    if ("locationId" in frame) return frame.locationId ?? null;
    return scene.locationId;
  }

  private frameAssets(scene: StudioScene, frame: SbFrame): string[] {
    if (Array.isArray(frame.assets)) return frame.assets;
    const refs = this.store.getRefsForScene(scene.id).filter((r) => r.type === "asset");
    return Array.from(new Set(refs.map((r) => r.entityId))).filter((id) =>
      this.store.get("assets").some((a) => a.id === id),
    );
  }

  private outfitLabel(frame: SbFrame, characterId: string): string | null {
    const lookId = frame.outfitByCharacter?.[characterId];
    if (!lookId) return null;
    const look = this.store.get("costumeLooksByCharacterId")[characterId]?.find(
      (l) => l.id === lookId,
    );
    return look?.note || "Outfit";
  }

  private setFrameCast(scene: StudioScene, frame: SbFrame, ids: string[]): void {
    this.updateFrame(scene.id, frame.id, { cast: ids });
  }

  private setFrameLocation(scene: StudioScene, frame: SbFrame, id: string | null): void {
    this.updateFrame(scene.id, frame.id, { locationId: id });
  }

  private setFrameAssets(scene: StudioScene, frame: SbFrame, ids: string[]): void {
    this.updateFrame(scene.id, frame.id, { assets: ids });
  }

  // ------------------------------------------------------------------
  // Frame mutations (via the foundation store)
  // ------------------------------------------------------------------

  private framesFor(sceneId: string): SbFrame[] {
    return (this.store.get("storyboardFramesBySceneId")[sceneId] ?? []) as SbFrame[];
  }

  private frameById(sceneId: string, frameId: string): SbFrame | null {
    return this.framesFor(sceneId).find((f) => f.id === frameId) ?? null;
  }

  private activeFrame(): { scene: StudioScene; frame: SbFrame } | null {
    if (!this.activeSceneId || !this.activeFrameId) return null;
    const scene = this.store.get("scenes").find((s) => s.id === this.activeSceneId);
    if (!scene) return null;
    const frame = this.frameById(this.activeSceneId, this.activeFrameId);
    if (!frame) return null;
    return { scene, frame };
  }

  private openFrame(sceneId: string, frameId: string): void {
    const scene = this.store.get("scenes").find((s) => s.id === sceneId);
    const frame = this.frameById(sceneId, frameId);
    if (!scene || !frame) return;
    this.activeSceneId = sceneId;
    this.activeFrameId = frameId;
    this.frameTab = "character";
    this.view = "frame";
    this.frameModalSubmitted = false;
    this.frameModalDeleted = false;
    this.frameSnapshot = JSON.parse(JSON.stringify(frame)) as SbFrame;
    const modal = openFormModal({
      title: `Shot details — ${scene.heading || "Untitled scene"}`,
      description: "Frame composition, cast, location, assets, and camera stay together here.",
      surface: "storyboard-frame",
      variant: "is-storyboard-frame",
      submitLabel: "Done",
      submitAction: "storyboard-frame-save",
      cancelAction: "storyboard-frame-cancel",
      initialFocus: '[data-koko-action="storyboard-back"]',
      onSubmit: () => {
        this.frameModalSubmitted = true;
        return true;
      },
      onClose: () => {
        if (!this.frameModalSubmitted && !this.frameModalDeleted && this.frameSnapshot) {
          this.updateFrame(sceneId, frameId, this.frameSnapshot);
        }
        this.frameModal = null;
        this.frameSnapshot = null;
        this.view = "grid";
        this.activeSceneId = null;
        this.activeFrameId = null;
        this.render();
      },
    });
    modal.root.dataset.sceneId = sceneId;
    modal.root.dataset.frameId = frameId;
    modal.submitButton.dataset.id = frameId;
    this.frameModal = modal;
    modal.body.appendChild(this.buildFrameInfo());
  }

  private closeFrame(): void {
    if (this.frameModal) {
      this.frameModal.close("cancel");
      return;
    }
    this.view = "grid";
    this.activeSceneId = null;
    this.activeFrameId = null;
    this.render();
  }

  private openAddFrameModal(scene: StudioScene): void {
    const caption = document.createElement("textarea");
    caption.className = "studio-textarea";
    caption.placeholder = "Describe the action or dialogue beat in this shot…";
    caption.dataset.kokoAction = "storyboard-new-caption";
    const preview = document.createElement("div");
    preview.className = "sb-create-preview";
    preview.innerHTML = '<span>New shot</span><strong>16:9 storyboard frame</strong>';
    const modal = openFormModal({
      title: `Add shot — ${scene.heading || "Untitled scene"}`,
      description: "Set the beat now; generation and camera choices remain available in Shot details.",
      surface: "storyboard-create",
      variant: "is-entity-form",
      initialFocus: caption,
      submitLabel: "Add shot",
      submitAction: "storyboard-create-save",
      cancelAction: "storyboard-create-cancel",
      onSubmit: () => {
        this.addFrame(scene.id, caption.value.trim());
        return true;
      },
    });
    modal.submitButton.dataset.id = scene.id;
    modal.body.append(preview, buildHelperParagraph("A concise visual beat makes later generation more controllable."), caption);
  }

  private addFrame(sceneId: string, caption = ""): void {
    const frames = this.framesFor(sceneId);
    const next: SbFrame = {
      id: `fr_${Date.now().toString(36)}${Math.floor(Math.random() * 1e4).toString(36)}`,
      caption,
      image: null,
      order: frames.length,
      retakes: 0,
      ready: false,
    };
    this.store.setStoryboardFrames(sceneId, [...frames, next]);
  }

  private updateFrame(sceneId: string, frameId: string, patch: Partial<SbFrame>): void {
    const frames = this.framesFor(sceneId).map((f) => (f.id === frameId ? { ...f, ...patch } : f));
    this.store.setStoryboardFrames(sceneId, frames);
  }

  private removeFrame(sceneId: string, frameId: string): void {
    const frames = this.framesFor(sceneId)
      .filter((f) => f.id !== frameId)
      .map((f, i) => ({ ...f, order: i }));
    this.store.setStoryboardFrames(sceneId, frames);
    this.recomputeStatus();
  }

  /** Reorder a dragged frame to sit at the target frame's position. */
  private reorder(sceneId: string, draggedId: string, targetId: string): void {
    if (draggedId === targetId) return;
    const frames = this.framesFor(sceneId).slice().sort((a, b) => a.order - b.order);
    const from = frames.findIndex((f) => f.id === draggedId);
    const to = frames.findIndex((f) => f.id === targetId);
    if (from < 0 || to < 0) return;
    const [moved] = frames.splice(from, 1);
    frames.splice(to, 0, moved);
    this.store.setStoryboardFrames(
      sceneId,
      frames.map((f, i) => ({ ...f, order: i })),
    );
  }

  private wireDrag(card: HTMLElement, sceneId: string, frameId: string): void {
    card.addEventListener("dragstart", (event) => {
      this.dragSceneId = sceneId;
      this.dragFrameId = frameId;
      card.classList.add("is-dragging");
      event.dataTransfer?.setData("text/plain", frameId);
      if (event.dataTransfer) event.dataTransfer.effectAllowed = "move";
    });
    card.addEventListener("dragend", () => {
      card.classList.remove("is-dragging");
      this.dragSceneId = null;
      this.dragFrameId = null;
    });
    card.addEventListener("dragover", (event) => {
      if (this.dragSceneId !== sceneId) return; // only reorder within a scene
      event.preventDefault();
      if (event.dataTransfer) event.dataTransfer.dropEffect = "move";
      card.classList.add("is-drop-target");
    });
    card.addEventListener("dragleave", () => card.classList.remove("is-drop-target"));
    card.addEventListener("drop", (event) => {
      event.preventDefault();
      card.classList.remove("is-drop-target");
      if (this.dragSceneId === sceneId && this.dragFrameId) {
        this.reorder(sceneId, this.dragFrameId, frameId);
      }
    });
  }

  private async generate(
    scene: StudioScene,
    frame: SbFrame,
    angleOverride?: CameraAngle,
  ): Promise<void> {
    if (this.prerequisites(scene).length > 0) return;
    const genKey = `${scene.id}:${frame.id}`;
    if (this.generating.has(genKey)) return;
    const isRetake = !!frame.image;
    this.generating.add(genKey);
    this.render();

    const angle = angleOverride ?? frame.cameraAngle ?? null;
    const direction = this.store.getVisualDirection();
    const prompt = [
      scene.heading,
      frame.caption,
      angle ? `camera ${describeShot(angle)}` : "",
      direction.notes.join(", "),
    ]
      .filter(Boolean)
      .join(" · ");

    const result = await runGeneration({
      kind: "image",
      prompt,
      seed: `${genKey}:${angle ? `${angle.pan},${angle.tilt},${angle.dolly}` : "base"}`,
      palette: direction.palette,
      aspect: "16:9",
    });

    this.generating.delete(genKey);
    const cur = this.frameById(scene.id, frame.id);
    this.updateFrame(scene.id, frame.id, {
      previousImage: cur?.image ?? null,
      image: result.url,
      ready: true,
      retakes: (cur?.retakes ?? 0) + (isRetake ? 1 : 0),
    });
    this.recomputeStatus();
  }

  // ------------------------------------------------------------------
  // Prereqs + overall status
  // ------------------------------------------------------------------

  private prerequisites(scene: StudioScene): Missing[] {
    const missing: Missing[] = [];
    const cast = this.store.getCastingForScene(scene.id);
    if (cast.length === 0) missing.push({ label: "cast", jumpTo: "script_editor" });
    if (!scene.locationId) missing.push({ label: "location", jumpTo: "location" });
    return missing;
  }

  private recomputeStatus(): void {
    const scenes = this.store.get("scenes");
    if (scenes.length === 0) return;
    const framesMap = this.store.get("storyboardFramesBySceneId");
    let anyReady = false;
    let allReady = true;
    for (const scene of scenes) {
      const frames = framesMap[scene.id] ?? [];
      const ready = frames.length > 0 && frames.every((f) => !!f.image);
      if (frames.some((f) => f.image)) anyReady = true;
      if (!ready) allReady = false;
      if (scene.storyboardReady !== ready) {
        this.store.updateScene(scene.id, scene.body, { storyboardReady: ready });
      }
    }
    this.store.setStoryboardStatus(allReady && anyReady ? "ready" : "empty");
  }
}
